import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Heading,
  Tag,
  Button,
  Select,
  SelectItem,
  SkeletonText,
  ToastNotification,
} from '@carbon/react';
import {
  Analytics,
  Download,
  Filter,
  User,
  Building,
  Education,
  Money,
  Checkmark,
  Warning,
  Information,
  View,
} from '@carbon/icons-react';
import adminService, { AdminStatistics } from '../../services/admin.service';

const ReportsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'daily' | 'month' | 'quarter' | 'year' | 'all'>('month');
  const [statistics, setStatistics] = useState<AdminStatistics | null>(null);
  const [notification, setNotification] = useState<{
    title: string;
    subtitle: string;
    kind: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  useEffect(() => {
    loadReportData();
  }, [dateRange]);

  const loadReportData = async () => {
    try {
      setLoading(true);
      const stats = await adminService.getStatistics(dateRange);
      setStatistics(stats);
    } catch (error) {
      setNotification({
        title: 'Erreur',
        subtitle: 'Impossible de charger les données du rapport',
        kind: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    if (num === null || num === undefined || isNaN(num)) {
      return '0';
    }
    return new Intl.NumberFormat('fr-DZ').format(num);
  };

  const getDateRangeLabel = () => {
    switch (dateRange) {
      case 'daily':
        return "Aujourd'hui";
      case 'month':
        return 'Ce mois';
      case 'quarter':
        return 'Ce trimestre';
      case 'year':
        return 'Cette année';
      default:
        return 'Tout';
    }
  };


 

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Heading className="text-3xl font-bold text-gray-900 mb-2">
            Rapports et analyses
          </Heading>
          <p className="text-gray-600">
            Analysez les performances de la plateforme PHINLEX
          </p>
        </div>
        <div className="flex space-x-3">
          <Button kind="tertiary" renderIcon={Filter}>
            Filtrer
          </Button>
          <Button kind="primary" renderIcon={Download}>
            Exporter rapport
          </Button>
        </div>
      </div>

      {/* Filters and Header */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-brand-primary/10 rounded-lg">
              <Analytics size={20} className="text-brand-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Statistiques de la plateforme</h2>
              <p className="text-sm text-gray-600">{getDateRangeLabel()}</p>
            </div>
          </div>
          <Select
            id="date-range"
            labelText="Période"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as any)}
            size="md"
            className="w-48"
          >
            <SelectItem value="daily" text="Aujourd'hui" />
            <SelectItem value="month" text="Ce mois" />
            <SelectItem value="quarter" text="Ce trimestre" />
            <SelectItem value="year" text="Cette année" />
            <SelectItem value="all" text="Tout" />
          </Select>
        </div>
      </div>

      {/* Key Metrics - 2 rows of 5 cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1">Total utilisateurs</p>
              <h3 className="text-xl font-bold text-gray-900">
                {statistics ? formatNumber(statistics.totalUsers) : '0'}
              </h3>
            </div>
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <User size={20} className="text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1">Trainees</p>
              <h3 className="text-xl font-bold text-gray-900">
                {statistics ? formatNumber(statistics.totalTrainees) : '0'}
              </h3>
            </div>
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <User size={20} className="text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1">Centres de formation</p>
              <h3 className="text-xl font-bold text-gray-900">
                {statistics ? formatNumber(statistics.totalTrainingCenters) : '0'}
              </h3>
            </div>
            <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Building size={20} className="text-purple-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1">Consultants</p>
              <h3 className="text-xl font-bold text-gray-900">
                {statistics ? formatNumber(statistics.totalConsultants) : '0'}
              </h3>
            </div>
            <div className="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <User size={20} className="text-yellow-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1">Services publiés</p>
              <h3 className="text-xl font-bold text-gray-900">
                {statistics ? formatNumber(statistics.totalServicesPublished) : '0'}
              </h3>
            </div>
            <div className="h-10 w-10 bg-cyan-100 rounded-lg flex items-center justify-center">
              <Education size={20} className="text-cyan-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1">Formations publiées</p>
              <h3 className="text-xl font-bold text-gray-900">
                {statistics ? formatNumber(statistics.totalFormationsPublished) : '0'}
              </h3>
            </div>
            <div className="h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Education size={20} className="text-indigo-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1">Vues services</p>
              <h3 className="text-xl font-bold text-gray-900">
                {statistics ? formatNumber(statistics.totalServiceViews) : '0'}
              </h3>
            </div>
            <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <View size={20} className="text-orange-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1">Vues formations</p>
              <h3 className="text-xl font-bold text-gray-900">
                {statistics ? formatNumber(statistics.totalTrainingViews) : '0'}
              </h3>
            </div>
            <div className="h-10 w-10 bg-pink-100 rounded-lg flex items-center justify-center">
              <View size={20} className="text-pink-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1">Inscriptions formations</p>
              <h3 className="text-xl font-bold text-gray-900">
                {statistics ? formatNumber(statistics.totalTrainingSubscriptions || 0) : '0'}
              </h3>
            </div>
            <div className="h-10 w-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <Checkmark size={20} className="text-teal-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1">Réservations services</p>
              <h3 className="text-xl font-bold text-gray-900">
                {statistics ? formatNumber(statistics.totalServiceSubscriptions || 0) : '0'}
              </h3>
            </div>
            <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Checkmark size={20} className="text-amber-600" />
            </div>
          </div>
        </div>
      </div>




      {/* Notification */}
      {notification && (
        <ToastNotification
          kind={notification.kind}
          title={notification.title}
          subtitle={notification.subtitle}
          onClose={() => setNotification(null)}
          timeout={5000}
        />
      )}
    </div>
  );
};

export default ReportsPage;
